
import { Button, Image, makeStyles, Text, tokens } from '@fluentui/react-components';
import { CheckmarkCircle24Regular, ArrowSync24Regular } from '@fluentui/react-icons';
import * as React from 'react'
import CheckLabel from '../../components/CheckLabel';
import { EmailInfo, useTabStore } from '../../providers/TabProvider';
import { rootUseStyles } from '../../taskpane/fluent-style';
import { Stack } from '../../components/Stack';

const useStyles = makeStyles({

    iconContainer: {
        height: "100px",
        width: "100px",
        backgroundColor: "#0A78D51f",
        alignContent: "center",
        borderRadius: "50%",
        textAlign: "center",
        overflow: "hidden",
    },
    icon: {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        marginTop: "10px",

    },
    header1: {
        fontSize: "20px",
        fontWeight: 600,
        margin: "10px 0",
        textAlign: "center",
    },
    paragraph: {
        margin: "5px 0",
    },
    section: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "25px",
        padding: "15px",
        height: "100vh",
    },
    titleSection: {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
    },
    info: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: "4px",
        backgroundColor: `${tokens.colorNeutralBackground1}`,
        padding: "10px",
    },
    alreadyScannedBanner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        padding: "12px",
        borderRadius: "6px",
        backgroundColor: "#E8F5E9",
        border: "1px solid #4CAF50",
        width: "100%",
    },
    alreadyScannedHeader: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    buttonGroup: {
        display: "flex",
        gap: "8px",
        width: "100%",
    },

});

interface WelcomeMessageProps {
    handleInfo: (data?: Partial<EmailInfo>) => void;
    alreadyScanned?: boolean;
    scannedAt?: string | null;
    onViewPreviousResults?: () => void;
}

export default function WelcomeMessage({
    handleInfo,
    alreadyScanned = false,
    scannedAt,
    onViewPreviousResults,
}: WelcomeMessageProps) {
    const emailData = useTabStore((s) => s.emailData);
    const styles = useStyles();

    const formatScannedDate = (dateStr?: string | null) => {
        if (!dateStr) return "previously";
        try {
            return new Date(dateStr).toLocaleString();
        } catch {
            return dateStr;
        }
    };

    return (
        <section className={`${styles.section} animated-background`}>

            <div className={styles.titleSection}>
                <div className={styles.iconContainer}>
                    <Image
                        width="90"
                        height="90"
                        src={"assets/filled-logo-dog.png"}
                        alt={"jasper-icon"}
                        className={styles.icon}
                    />
                </div>
                <div>
                    <Text as="h1" block className={styles.header1}>
                        {alreadyScanned ? "Email Already Scanned" : "Scan this email for phishing"}
                    </Text>
                    <Text as="p" block align='center' className={styles.paragraph}>
                        {alreadyScanned
                            ? "This email was previously analyzed. You can view the results or scan again."
                            : "Scan this email for potential threats and receive actionable security insights and recommendations."
                        }
                    </Text>
                </div>
            </div>

            {/* Already Scanned Banner */}
            {alreadyScanned && (
                <div className={styles.alreadyScannedBanner}>
                    <div className={styles.alreadyScannedHeader}>
                        <CheckmarkCircle24Regular primaryFill="#4CAF50" />
                        <Text weight="semibold" style={{ color: "#2E7D32" }}>
                            Scanned on {formatScannedDate(scannedAt)}
                        </Text>
                    </div>
                    <div className={styles.buttonGroup}>
                        <Button
                            appearance="primary"
                            style={{ flex: 1 }}
                            onClick={onViewPreviousResults}
                        >
                            View Results
                        </Button>
                        <Button
                            appearance="outline"
                            icon={<ArrowSync24Regular />}
                            style={{ flex: 1 }}
                            onClick={() => handleInfo(emailData)}
                        >
                            Rescan
                        </Button>
                    </div>
                </div>
            )}

            {/* Info box - show only for first scan */}
            {!alreadyScanned && (
                <div className={styles.info}>
                    <Text as="p" weight='bold' block align='center' className={styles.paragraph}>
                        What does this do?
                    </Text>
                    <CheckLabel label="Analyzes email content, links, and attachments for threats" />
                    <CheckLabel label="Provides security recommendations based on scan results" />
                    <CheckLabel label="Offers detailed technical analysis for security teams" />
                </div>
            )}

            {/* Scan button - show only for first scan */}
            {!alreadyScanned && (
                <Stack gap={10}>
                    <Button
                        disabled={Object.keys(emailData).length == 0}
                        onClick={() => {
                            handleInfo(emailData);
                        }}
                        style={{
                            width: "140px",
                            padding: "8px 0px"
                        }}
                        appearance="primary"
                    >
                        Scan This Email
                    </Button>
                    <Button
                        disabled={Object.keys(emailData).length == 0}
                        appearance="outline"
                        onClick={() => {
                            // @ts-ignore
                            useTabStore.getState().setActiveTab("overview");
                        }}
                        style={{
                            width: "140px",
                            color: "#0A78D5",
                            border: "1px solid #0A78D5",
                            padding: "8px 0px"
                        }}
                    >
                        Overview
                    </Button>
                </Stack>
            )}
        </section>
    );
}
